import {
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
  type QueryFunctionContext,
  type UseQueryResult,
} from '@tanstack/react-query'

import { newsKeys } from '@/features/news/api/keys'
import { marketNewsService } from '@/features/news/api/marketNewsService'
import { newsService } from '@/features/news/api/newsService'
import type {
  NewsArticle,
  NewsArticlePage,
  NewsArticlesQuery,
  NewsLang,
} from '@/schemas/market-news'
import type { NewsFeedQuery, NewsPage } from '@/schemas/news'

/**
 * Market update's story feed.
 *
 * Cursor paging, not the offset pager the Market tables use: this is a feed
 * ordered by time and it grows at the head, so an offset page would re-serve a
 * story the member has already read the moment a new one lands. Same shape as
 * `useHappeningFeed`, so the "Load more" footer is the same control.
 *
 * The factory carries the pagination shape (`initialPageParam`,
 * `getNextPageParam`) alongside the key and fetch, unlike a plain query's
 * factory: an infinite query's cache entry is an `InfiniteData` object keyed
 * on how it pages, and `/market/update`'s loader has to produce that same
 * shape or `useNewsFeed` finds a cache entry it does not recognise. There is
 * no `infiniteQueryOptions` helper in this version of react-query, so this is
 * a plain object rather than `queryOptions(...)`.
 */
export const newsFeedQueryOptions = (query: NewsFeedQuery) => ({
  queryKey: newsKeys.feed(query),
  initialPageParam: undefined as string | undefined,
  queryFn: ({
    pageParam,
    client,
  }: QueryFunctionContext<ReturnType<typeof newsKeys.feed>, string | undefined>) =>
    newsService.feed(query, pageParam, client),
  getNextPageParam: (last: NewsPage) => last.nextCursor,
})

export const useNewsFeed = (query: NewsFeedQuery) =>
  useInfiniteQuery({
    ...newsFeedQueryOptions(query),
    // An unserved sort or filter is never asked while mm-be has no param for it; the feed draws
    // the no-data state from the same check (`newsService.canRequest`).
    enabled: newsService.canRequest(query),
    // A scope chip or a search keystroke must not empty the feed while the next
    // one loads — the stories the member is reading stay until it arrives.
    placeholderData: keepPreviousData,
  })

/**
 * The Filters dialog's "Show N stories", for the draft it holds: `undefined` while it loads, for
 * `null` (no count wanted), and for a draft mm-be cannot answer (`newsService.canRequest`) — never
 * the previous draft's number under a draft it does not describe.
 */
export const useNewsCount = (query: NewsFeedQuery | null): number | undefined => {
  const enabled = query !== null && newsService.canRequest(query)
  const count = useQuery({
    queryKey: newsKeys.count(query),
    queryFn: ({ client }) => newsService.count(query ?? { scope: 'all', sort: 'newest' }, client),
    enabled,
    placeholderData: keepPreviousData,
  })
  return enabled ? count.data : undefined
}

/** Market › News's list. Never fires while `marketNewsService.available` is false. */
export const newsStoriesQueryOptions = (query: NewsArticlesQuery) => ({
  queryKey: newsKeys.stories(query),
  initialPageParam: undefined as string | undefined,
  queryFn: ({
    pageParam,
  }: QueryFunctionContext<ReturnType<typeof newsKeys.stories>, string | undefined>) =>
    marketNewsService.stories(query, pageParam),
  getNextPageParam: (last: NewsArticlePage) => last.nextCursor,
})

export const useNewsStories = (query: NewsArticlesQuery) =>
  useInfiniteQuery({
    ...newsStoriesQueryOptions(query),
    enabled: marketNewsService.available,
    placeholderData: keepPreviousData,
  })

/** One story: the preview dialog (`id` set) and the article page. */
export const newsArticleQueryOptions = (id: string, lang: NewsLang) =>
  queryOptions<NewsArticle>({
    queryKey: newsKeys.article(id, lang),
    queryFn: () => marketNewsService.article(id, lang),
  })

export const useNewsArticle = (id: string | null, lang: NewsLang): UseQueryResult<NewsArticle> =>
  useQuery({
    ...newsArticleQueryOptions(id ?? '', lang),
    enabled: marketNewsService.available && id !== null,
  })
